import { processIntelReportFilters } from '@pn/core/domain/intel/intel-report';
import {
  buildIntelQueryObject,
  createIntelWorkspaceItem,
} from '@pn/core/domain/intel/intel-workspace-item';
import type { Filter } from '@pn/core/domain/query';
import { useIntelAccess } from '@pn/core/permissions/isIntelAccessDenied';
import { useCompaniesStorage } from '@pn/core/storage';
import { apiIntelDynamicDataProvider } from '@pn/services/api/intel-data/apiIntelDataProvider';
import { apiIntelReportsProvider } from '@pn/services/api/intel-report/apiIntelReportsProvider';
import { isEmpty, isNil } from 'lodash-es';
import React from 'react';
import { generateIntelQueriesForReport } from 'src/application/intel-templates/activityOverview';
import {
  intelReportsActions,
  intelWorkspaceItemsActions,
  useIntelReportsStorage,
} from 'src/application/storage';
import { useBOEIntel } from 'src/ui/contexts/useBOEIntel';

export function useLoadReportData() {
  const access = useIntelAccess();
  const isAccessDeniedToReports = access('reports').denied();
  const { possibleLandSaleDates } = useBOEIntel();
  const { reportToLoad: report } = useIntelReportsStorage();
  const { companyNameToIdRecord } = useCompaniesStorage();

  React.useEffect(() => {
    if (!report || isEmpty(possibleLandSaleDates)) return;
    const reportId = report.id;
    intelReportsActions().markReportAsLoaded(report.id);
    intelWorkspaceItemsActions().removeByReportId(report.id); // clears all existing queries for this report to refresh the data (used when editing)
    const intelQueriesForReport = generateIntelQueriesForReport(
      report,
      companyNameToIdRecord,
      possibleLandSaleDates[0]
    );

    intelWorkspaceItemsActions().add(intelQueriesForReport);
    if (isAccessDeniedToReports) return; // no freeloaders allowed

    const {
      geoJSONGeometry,
      companyBrandName,
      formation,
      fieldName,
      startDate,
      endDate,
      productionMonthToConsider,
      historicalDataStartDate,
    } = processIntelReportFilters(report.filters, companyNameToIdRecord);

    const payload: any = {
      start_date: startDate,
      end_date: endDate,
    };
    const wellFilters = [];
    if (geoJSONGeometry) {
      payload.geo_shape = geoJSONGeometry;
    }
    if (companyBrandName) {
      payload.company_brand_name = companyBrandName;
      wellFilters.push({
        field: 'company_brand.display_name',
        operator: '=',
        value: companyBrandName,
      });
    }
    if (formation) {
      payload.formation = formation;
      wellFilters.push({
        field: 'formation',
        operator: '=',
        value: formation,
      });
    }
    if (fieldName) {
      payload.field_name = fieldName;
      wellFilters.push({
        field: 'field_name',
        operator: '=',
        value: fieldName,
      });
    }
    intelReportsActions().setIsLoadingWells(reportId);
    getWellData(
      [
        {
          field: 'last_spud_date',
          operator: '>=',
          value: historicalDataStartDate,
        },
        ...wellFilters,
      ],
      geoJSONGeometry
    ).then((monthlySpuddedLicenses) => {
      intelReportsActions().setMonthlySpuddedLicenses(
        reportId,
        monthlySpuddedLicenses
      );
    });
    getWellData(
      [
        {
          field: 'licenses.license_date',
          operator: '>=',
          value: historicalDataStartDate,
        },
        ...wellFilters,
      ],
      geoJSONGeometry
    ).then((monthlyLicenses) => {
      intelReportsActions().setMonthlyLicences(reportId, monthlyLicenses);
    });

    apiIntelReportsProvider.getWellsYearOverYear(payload).then((report) => {
      intelReportsActions().setYearOverYearData(
        reportId,
        report.spudded_licences_count_yoy,
        report.licenced_count_yoy
      );
    });

    if (!isNil(productionMonthToConsider) && isNil(geoJSONGeometry)) {
      payload.production_month_considered = productionMonthToConsider;
      intelReportsActions().setIsLoadingProduction(reportId);
      apiIntelReportsProvider
        .getMonthsProduction(payload)
        .then((production) => {
          intelReportsActions().setProduction(reportId, production);
        });
    }
  }, [
    companyNameToIdRecord,
    isAccessDeniedToReports,
    possibleLandSaleDates,
    report,
  ]);
}

async function getWellData(filters: Filter[], geometry?: GeoJSON.Geometry) {
  return apiIntelDynamicDataProvider.getData({
    intelWorkspaceItem: createIntelWorkspaceItem({
      name: 'Licence Activity',
      query: buildIntelQueryObject('well_licenses', {
        filters,
      }),
    }),
    ...(geometry ? { geometry } : undefined),
  });
}
